import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import logoLimpia from '../assets/images/logo-limpia-xl.png';

// URL base para imágenes (Misma lógica que ActaVisitaPDF)
const getBaseUrl = () => {
  const URL = process.env.REACT_APP_API_URL || '';
  if (URL.includes("/api")) {
    const url = URL.split("/api");
    return url[0];
  }
  return URL;
};

// Función de formato de fecha
const formatFecha = (fecha) => {
  if (!fecha || fecha === 'null' || fecha === 'undefined') return '';
  try {
    let date;
    // Si viene en formato YYYY-MM-DD (ISO), parseamos manualmente para evitar desfase de zona horaria
    if (typeof fecha === 'string' && fecha.includes('-') && fecha.split('-').length === 3) {
      const parts = fecha.split('T')[0].split('-');
      date = new Date(parts[0], parts[1] - 1, parts[2]); // Año, Mes (0-11), Día
    } else {
      date = new Date(fecha);
    }

    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return '';
  }
};

const formatHora = (hora) => {
  if (!hora || hora === 'null' || hora === 'undefined') return '';
  try {
    if (typeof hora === 'string' && hora.includes(':')) {
      const parts = hora.split(':');
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]);
        const minutes = parts[1];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHour = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
        return `${displayHour}:${minutes} ${ampm}`;
      }
    }
    const date = new Date(hora);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
    return '';
  } catch {
    return '';
  }
};

// Componente robusto para imágenes de firma
const SignatureImage = ({ src, style }) => {
  if (!src) return null;

  let finalSrc = src;

  // Si es un string y no es base64 ni blob, asumir ruta relativa
  if (typeof src === 'string') {
    if (!src.startsWith('http') && !src.startsWith('data:') && !src.startsWith('blob:')) {
      const baseUrl = getBaseUrl();
      const cleanPath = src.startsWith('/') ? src : `/${src}`;
      finalSrc = `${baseUrl}${cleanPath}`;
    }
  }

  return <Image src={finalSrc} style={style} />;
};

// Estilos corporativos refinados
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 15,
    fontSize: 8,
  },
  // Contenedor principal
  mainContainer: {
    borderWidth: 1.5,
    borderColor: '#495057',
    borderRadius: 0,
    padding: 8,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderStyle: 'solid',
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    borderBottomWidth: 1.5,
    borderBottomColor: '#495057',
    borderBottomStyle: 'dashed',
    paddingBottom: 6,
  },
  headerLeft: {
    width: '25%',
  },
  headerCenter: {
    width: '50%',
    alignItems: 'center',
  },
  headerRight: {
    width: '25%',
    alignItems: 'flex-end',
  },
  logo: {
    width: 130,
    height: 52,
    objectFit: 'contain',
  },
  title: {
    fontSize: 13,
    fontWeight: 'extrabold',
    color: '#28a745',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 7,
    color: '#6c757d',
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
  ticketNumberBox: {
    borderWidth: 1,
    borderColor: '#28a745',
    borderStyle: 'dashed',
    padding: 4,
    borderRadius: 2,
    backgroundColor: '#f1f8f3',
  },
  ticketNumber: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#28a745',
  },

  // Grid de contenido
  row: {
    flexDirection: 'row',
    marginBottom: 2,
    gap: 10,
  },
  col: {
    flex: 1,
  },

  // Estilos de Sección Genéricos
  sectionContainer: {
    marginBottom: 3,
    paddingLeft: 6,
    borderLeftWidth: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  sectionTitle: {
    fontSize: 9.5,
    fontWeight: 'bold',
    marginBottom: 3,
    textTransform: 'uppercase',
    borderBottomWidth: 0.5,
    borderBottomStyle: 'dashed',
    paddingBottom: 1,
    width: '100%',
  },

  // Variaciones de color por sección
  remitenteSection: {
    borderLeftColor: '#28a745', // Verde
    backgroundColor: '#f6fff8',
  },
  remitenteTitle: {
    color: '#28a745',
    borderBottomColor: '#28a745',
  },

  destinatarioSection: {
    borderLeftColor: '#dc3545', // Rojo
    backgroundColor: '#fff8f9',
  },
  destinatarioTitle: {
    color: '#dc3545',
    borderBottomColor: '#dc3545',
  },

  entregaSection: {
    borderLeftColor: '#17a2b8', // Azul
    backgroundColor: '#f0fbff',
  },
  entregaTitle: {
    color: '#17a2b8',
    borderBottomColor: '#17a2b8',
  },

  // Campos
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 1.5,
    alignItems: 'baseline',
  },
  fieldLabel: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: '#555',
    width: 55,
  },
  fieldValue: {
    fontSize: 8.5,
    color: '#000',
    flex: 1,
  },

  remitenteAddresses: {
    flex: 1,
    marginTop: 1,
  },
  remitenteAddressItem: {
    fontSize: 7.5,
    color: '#000',
    marginBottom: 1,
  },

  // Línea de corte punteada
  cutLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    borderBottomStyle: 'dotted',
    marginBottom: 3,
    marginTop: 2,
  },

  // Firmas
  signaturesRow: {
    flexDirection: 'row',
    marginBottom: 3,
    gap: 15,
    paddingTop: 3,
    borderTopWidth: 1.5,
    borderTopColor: '#e9ecef',
    borderTopStyle: 'dashed',
  },
  signatureBox: {
    flex: 1,
    alignItems: 'center',
    height: 60,
    justifyContent: 'flex-end',
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#dee2e6',
    borderStyle: 'dashed',
    borderRadius: 4,
    paddingBottom: 4,
  },
  signatureImage: {
    height: 35,
    width: 100,
    objectFit: 'contain',
    marginBottom: 2,
  },
  signatureLine: {
    width: '80%',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    marginBottom: 2,
  },
  signatureLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 4,
  },

  // Footer con fondo diferente (NO VERDE)
  footer: {
    backgroundColor: '#e9ecef', // Gris claro
    padding: 4,
    borderRadius: 2,
    marginTop: 3,
    borderWidth: 0.5,
    borderColor: '#ced4da',
  },
  footerText: {
    fontSize: 6.5,
    color: '#495057', // Texto oscuro para contraste
    textAlign: 'justify',
    lineHeight: 1.2,
    marginBottom: 1,
  },
  footerLinks: {
    fontSize: 6,
    marginTop: 2,
    color: '#495057',
    textAlign: 'justify',
    lineHeight: 1.2,
    marginBottom: 1,
  },
  footerCode: {
    fontSize: 7,
    color: '#000',
    fontWeight: 'bold',
    textAlign: 'right',
    marginTop: 1,
  },

  // Estilos para Plazos Destacados
  plazosContainer: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    backgroundColor: '#fff3cd', // Un tono amarillo muy suave para resaltar fechas
    padding: 3,
    marginBottom: 3,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '#ffc107',
    borderStyle: 'dotted',
  },
  plazoItem: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#212529',
  },
  plazoLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#856404',
    marginRight: 4,
    textTransform: 'uppercase',
  },

  // CAUSAS DE DEVOLUCIÓN (Tabla visual)
  devolucionSection: {
    marginTop: 3,
    marginBottom: 3,
    paddingLeft: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#ffc107', // Amarillo
    backgroundColor: '#fffdf2',
    paddingTop: 2,
    paddingBottom: 2,
  },
  devolucionHeader: {
    marginBottom: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#ffc107',
    borderBottomStyle: 'dashed',
    paddingBottom: 1,
  },
  devolucionTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#d39e00',
    textAlign: 'center',
  },
  devolucionRow: {
    flexDirection: 'row',
    marginBottom: 2,
    alignItems: 'center',
  },
  devolucionCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 4,
  },
  devolucionCellLast: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 4,
  },
  devolucionLabel: {
    fontSize: 7.5,
    color: '#555',
    fontWeight: 'bold',
  },
  checkSquare: {
    width: 8,
    height: 8,
    borderWidth: 0.5,
    borderColor: '#d39e00',
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  obsSectionContainer: {
    marginTop: 4,
    paddingLeft: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#ffc107',
    backgroundColor: '#fffdf2',
    paddingTop: 2,
    paddingBottom: 2,
  },
  watermarkContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.25,
    zIndex: 10,
    transform: 'rotate(-15deg)',
  },
  watermarkText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#dc3545',
    borderWidth: 1.5,
    borderColor: '#dc3545',
    padding: 3,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  selloWatermarkSignature: {
    position: 'absolute',
    top: '15%',
    left: '5%',
    right: '5%',
    borderWidth: 1.5,
    borderColor: '#dc3545',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    padding: 4,
    zIndex: 100,
    transform: 'rotate(-5deg)',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
  },
  selloWatermarkSignatureText: {
    fontSize: 8,
    color: '#dc3545',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  stampPage: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
});

const TiqueteCorrespondenciaPDF = ({ data }) => {
  // Datos de visitas
  const visitas = data?.visitas || [];
  const causas = data?.causas || {};

  const codigoGuia = `N° Guia 30${data?.planilla ? ' ' + data.planilla : ''}${data?.no_resolucion ? ' ' + data.no_resolucion : ''}`;

  // Helper para resolver URL de imágenes
  const resolveImgUrl = (src) => {
    if (!src) return null;
    if (src.startsWith('data:') || src.startsWith('http')) return src;
    return getBaseUrl() + (src.startsWith('/') ? '' : '/') + src;
  };

  const mapImageSrc = data?.locationImage ? resolveImgUrl(data.locationImage) : null;
  const selloImageSrc = data?.selloImage ? resolveImgUrl(data.selloImage) : null;
  const fotosImages = data?.fotosImages || [];

  return (
    <Document>
      {/* ===== PÁGINA 1: TIQUETE COMPLETO + MAPA + SELLO ===== */}
      <Page size="LETTER" style={styles.page}>
        <View style={styles.mainContainer}>

          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Image src={logoLimpia} style={styles.logo} />
            </View>
            <View style={styles.headerCenter}>
              <Text style={styles.title}>TIQUETE DE CORRESPONDENCIA</Text>
              <Text style={styles.subtitle}>CONTROL DE ENVÍO Y RECEPCIÓN</Text>
            </View>
            <View style={styles.headerRight}>
              <View style={styles.ticketNumberBox}>
                <Text style={styles.ticketNumber}>{codigoGuia}</Text>
              </View>
            </View>
          </View>

          {/* Plazos Destacados */}
          <View style={styles.plazosContainer}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={styles.plazoLabel}>FECHA DE EMISION:</Text>
              <Text style={styles.plazoItem}>{formatFecha(data?.fecha_original) || 'N/A'}</Text>
            </View>
            <Text style={{ color: '#adb5bd' }}>|</Text>
            <View style={{ flexDirection: 'row' }}>
              <Text style={styles.plazoLabel}>FECHA DE ENVIO:</Text>
              <Text style={styles.plazoItem}>{formatFecha(data?.fecha_original) || 'N/A'}</Text>
            </View>
            <Text style={{ color: '#adb5bd' }}>|</Text>
            <View style={{ flexDirection: 'row' }}>
              <Text style={styles.plazoLabel}>FECHA LIMITE DE EJECUCION:</Text>
              <Text style={styles.plazoItem}>{formatFecha(data?.fecha_limite) || 'N/A'}</Text>
            </View>
          </View>

          {/* Contenido Principal - 2 Columnas */}
          <View style={styles.row}>
            {/* Columna Izquierda */}
            <View style={styles.col}>

              {/* Remitente */}
              <View style={[styles.sectionContainer, styles.remitenteSection]}>
                <Text style={[styles.sectionTitle, styles.remitenteTitle]}>REMITENTE</Text>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Entidad:</Text>
                  <Text style={styles.fieldValue}>Ciudad Limpia</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Direcciones:</Text>
                  <View style={styles.remitenteAddresses}>
                    <Text style={[styles.remitenteAddressItem, { fontWeight: 'bold' }]}>Centro de Atención al Usuario. Av Boyacá No. 6B – 20/28</Text>
                    <Text style={[styles.remitenteAddressItem, { marginBottom: 0.5 }]}>Horario: L – V de 7:00 am a 5:00 pm – sábados 9:00 am a 1:00 pm</Text>
                    <Text style={[styles.remitenteAddressItem, { fontWeight: 'bold', marginTop: 1 }]}>SuperCade de las Américas. Avenida Carrera 86 No. 43 – 55 Sur</Text>
                    <Text style={[styles.remitenteAddressItem, { marginBottom: 0.5 }]}>Horario: L – V de 7:00 am a 12:00 m y de 1:00 pm a 4:30 pm – sábados 8:00 am a 12:00 m</Text>
                    <Text style={[styles.remitenteAddressItem, { fontWeight: 'bold', marginTop: 1 }]}>Cade Fontibón. Diagonal 16 No. 104 – 51 Of 101 – C.C. Viva Fontibón</Text>
                    <Text style={[styles.remitenteAddressItem, { marginBottom: 0.5 }]}>Horario: L – V de 7:00 am a 12:00 m y de 1:00 pm a 4:30 pm</Text>
                    <Text style={[styles.remitenteAddressItem, { fontWeight: 'bold', marginTop: 1 }]}>Cade Kennedy. Carrera 78K No. 36 – 55 Sur</Text>
                    <Text style={[styles.remitenteAddressItem, { marginBottom: 0.5 }]}>Horario: L – V de 7:00 am a 12:00 m y de 1:00 pm a 4:30 pm</Text>
                  </View>
                </View>
              </View>

              <View style={styles.cutLine} />

              {/* Entrega (Azul) */}
              <View style={[
                styles.sectionContainer,
                styles.entregaSection,
                data?.conSello && { borderLeftColor: '#6c757d', backgroundColor: '#f8f9fa', opacity: 0.4 }
              ]}>
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 3,
                  borderBottomWidth: 0.5,
                  borderBottomStyle: 'dashed',
                  borderBottomColor: data?.conSello ? '#6c757d' : '#17a2b8',
                  paddingBottom: 1
                }}>
                  <Text style={[
                    styles.sectionTitle,
                    { marginBottom: 0, borderBottomWidth: 0, width: 'auto' },
                    data?.conSello ? { color: '#6c757d' } : styles.entregaTitle
                  ]}>
                    DATOS DE QUIEN RECIBE
                  </Text>
                  {data?.conSello && (
                    <View style={{ backgroundColor: '#6c757d', paddingHorizontal: 4, paddingVertical: 1, borderRadius: 2 }}>
                      <Text style={{ fontSize: 5.5, color: '#565656ff', fontWeight: 'bold' }}>ENTREGA CON SELLO</Text>
                    </View>
                  )}
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Nombre:</Text>
                  <Text style={styles.fieldValue}>{data?.conSello ? '' : (data?.recibidoNombre || 'Pendiente')}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Cédula:</Text>
                  <Text style={styles.fieldValue}>{data?.conSello ? '' : (data?.recibidoCedula || 'N/A')}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Teléfono:</Text>
                  <Text style={styles.fieldValue}>{data?.conSello ? '' : (data?.recibidoTelefono || 'N/A')}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Fecha y Hora:</Text>
                  <Text style={styles.fieldValue}>
                    {formatFecha(data?.fecha_informe) || formatFecha(data?.informe?.fecha_informe)} {formatHora(data?.hora_visita) || formatHora(data?.informe?.hora_visita)}
                  </Text>
                </View>
              </View>

            </View>

            {/* Columna Derecha */}
            <View style={styles.col}>

              {/* Destinatario */}
              <View style={[styles.sectionContainer, styles.destinatarioSection]}>
                <Text style={[styles.sectionTitle, styles.destinatarioTitle]}>DESTINATARIO</Text>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Nombre:</Text>
                  <Text style={styles.fieldValue}>{data?.destinatarioNombre || 'N/A'}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Dirección:</Text>
                  <Text style={styles.fieldValue}>{data?.destinatarioDireccion || 'N/A'}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Teléfono:</Text>
                  <Text style={styles.fieldValue}>{data?.destinatarioTelefono || 'N/A'}</Text>
                </View>
              </View>

              <View style={styles.cutLine} />

              {/* CAUSAS DE DEVOLUCIÓN */}
              <View style={styles.devolucionSection}>
                <View style={styles.devolucionHeader}>
                  <Text style={styles.devolucionTitle}>CAUSAS DE DEVOLUCIÓN</Text>
                </View>

                <View style={styles.devolucionRow}>
                  <View style={styles.devolucionCell}>
                    <Text style={styles.devolucionLabel}>Vacío</Text>
                    <View style={styles.checkSquare}>
                      {causas?.vacío && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                  <View style={styles.devolucionCellLast}>
                    <Text style={styles.devolucionLabel}>No hay quien reciba</Text>
                    <View style={styles.checkSquare}>
                      {causas?.noHayQuienReciba && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                </View>

                <View style={styles.devolucionRow}>
                  <View style={styles.devolucionCell}>
                    <Text style={styles.devolucionLabel}>Rehusado</Text>
                    <View style={styles.checkSquare}>
                      {causas?.rehusado && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                  <View style={styles.devolucionCellLast}>
                    <Text style={styles.devolucionLabel}>Dirección errada</Text>
                    <View style={styles.checkSquare}>
                      {causas?.direccionErrada && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                </View>

                <View style={styles.devolucionRow}>
                  <View style={styles.devolucionCell}>
                    <Text style={styles.devolucionLabel}>Alto riesgo</Text>
                    <View style={styles.checkSquare}>
                      {causas?.altoRiesgo && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                  <View style={styles.devolucionCellLast}>
                    <Text style={styles.devolucionLabel}>Correo certificado</Text>
                    <View style={styles.checkSquare}>
                      {causas?.correoCertificado && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                </View>

                <View style={[styles.devolucionRow, { marginBottom: 0 }]}>
                  <View style={[styles.devolucionCell, { borderRightWidth: 0 }]}>
                    <Text style={styles.devolucionLabel}>Error en los datos</Text>
                    <View style={styles.checkSquare}>
                      {causas?.errorDatos && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                  <View style={[styles.devolucionCellLast]}>
                    <Text style={styles.devolucionLabel}>Otros</Text>
                    <View style={styles.checkSquare}>
                      {causas?.otros && <Text style={{ fontSize: 6 }}>X</Text>}
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.cutLine} />

              {/* OBSERVACIONES */}
              <View style={styles.devolucionSection}>
                <View style={styles.devolucionHeader}>
                  <Text style={styles.devolucionTitle}>OBSERVACIONES</Text>
                </View>
                <Text style={{ fontSize: 7, color: '#000', minHeight: 20, textAlign: 'justify', marginTop: 2 }}>
                  {data?.observaciones && String(data.observaciones).replace(/null/g, '').trim() || 'Sin observaciones registradas.'}
                </Text>
                {visitas.length > 0 && (
                  <Text style={{ fontSize: 6, color: '#666', marginTop: 2, fontStyle: 'italic' }}>
                    * {visitas.length} visita(s) previa(s) registrada(s).
                  </Text>
                )}
              </View>

            </View>
          </View>

          {/* Firmas */}
          <View style={styles.signaturesRow}>
            <View style={[styles.signatureBox, data?.conSello && { backgroundColor: '#fdf2f2', borderColor: '#dc3545', borderStyle: 'dashed' }]}>
              {!data?.conSello && data?.representante_firma && (
                <SignatureImage src={data.representante_firma} style={styles.signatureImage} />
              )}
              <View style={[styles.signatureLine, data?.conSello && { borderBottomColor: '#dc3545', opacity: 0.4 }]} />
              <Text style={[styles.signatureLabel, data?.conSello && { color: '#dc3545' }]}>
                FIRMA REPRESENTANTE
              </Text>
              {data?.conSello && (
                <View style={styles.selloWatermarkSignature}>
                  <Text style={styles.selloWatermarkSignatureText}>RECIBIDO CON SELLO</Text>
                </View>
              )}
            </View>
            <View style={styles.signatureBox}>
              {data?.ciudad_limpia_firma && (
                <SignatureImage src={data.ciudad_limpia_firma} style={styles.signatureImage} />
              )}
              <View style={styles.signatureLine} />
              <Text style={styles.signatureLabel}>FIRMA FUNCIONARIO</Text>
              {(data?.ciudad_limpia_nombre) && (
                <Text style={{ fontSize: 6, color: '#666', textAlign: 'center', marginTop: 2 }}>
                  {data.ciudad_limpia_nombre}{data.ciudad_limpia_codigo ? ` (${data.ciudad_limpia_codigo})` : ''}
                </Text>
              )}
            </View>
          </View>

          {/* ===== CERTIFICACIÓN GEOGRÁFICA ===== */}
          {mapImageSrc && (
            <View style={{ marginTop: 2, marginBottom: 2 }}>
              {/* Título */}
              <View style={{
                backgroundColor: '#f8f9fa',
                padding: 4,
                borderRadius: 3,
                borderWidth: 1.5,
                borderColor: '#28a745',
                marginBottom: 4,
                borderStyle: 'solid',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#28a745', textTransform: 'uppercase' }}>
                  CERTIFICACIÓN GEOGRÁFICA DE ENTREGA
                </Text>
                {data?.conSello && selloImageSrc && (
                  <Text style={{ fontSize: 7, color: '#6c757d', marginLeft: 8 }}> | SELLO DE RECIBIDO</Text>
                )}
              </View>

              {/* SIN SELLO: mapa ancho completo (como antes) */}
              {(!data?.conSello || !selloImageSrc) && (
                <>
                  <View style={{
                    width: '100%',
                    height: 180,
                    borderWidth: 1.5,
                    borderColor: '#28a745',
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                  }}>
                    <Image
                      src={mapImageSrc}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </View>
                  {data?.locationCoords && (
                    <Text style={{ fontSize: 5.5, color: '#666', marginTop: 2, textAlign: 'right' }}>
                      GPS: {data.locationCoords}
                    </Text>
                  )}
                </>
              )}

              {/* CON SELLO: 2 columnas — mapa (60%) | sello (40%) */}
              {data?.conSello && selloImageSrc && (
                <View style={{ flexDirection: 'row', gap: 8 }}>

                  {/* Columna Mapa */}
                  <View style={{ flex: 3 }}>
                    <Text style={{ fontSize: 6, color: '#28a745', fontWeight: 'bold', marginBottom: 2, textAlign: 'center' }}>
                      📍 Ubicación GPS
                    </Text>
                    <View style={{
                      borderWidth: 1.5,
                      borderColor: '#28a745',
                      borderRadius: 3,
                      overflow: 'hidden',
                      backgroundColor: '#ffffff',
                      height: 150,
                    }}>
                      <Image
                        src={mapImageSrc}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </View>
                    {data?.locationCoords && (
                      <Text style={{ fontSize: 5.5, color: '#666', marginTop: 2, textAlign: 'center' }}>
                        GPS: {data.locationCoords}
                      </Text>
                    )}
                  </View>

                  {/* Columna Sello */}
                  <View style={{ flex: 2 }}>
                    <Text style={{ fontSize: 6, color: '#6c757d', fontWeight: 'bold', marginBottom: 2, textAlign: 'center' }}>
                      🔖 Sello de Recibido
                    </Text>
                    <View style={{
                      borderWidth: 1.5,
                      borderColor: '#6c757d',
                      borderStyle: 'dashed',
                      borderRadius: 3,
                      overflow: 'hidden',
                      backgroundColor: '#f8f9fa',
                      height: 150,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <Image
                        src={selloImageSrc}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </View>
                    <Text style={{ fontSize: 5.5, color: '#6c757d', marginTop: 2, textAlign: 'center' }}>
                      Sello de recepción
                    </Text>
                  </View>

                </View>
              )}
            </View>
          )}

          {/* Footer — marginTop auto lo ancla siempre al pie de página */}
          <View style={[styles.footer, { marginTop: 'auto' }]}>
            <Text style={styles.footerText}>
              Autorizo de manera previa, expresa e informada a Ciudad Limpia para que directa o indirectamente, realice cualquier
              operación de tratamiento de mis Datos Personales, a nivel nacional e internacional, incluso con terceros ubicados en
              países que no proporcionen niveles adecuados de protección conforme a lo dispuesto en la Ley 1581 de 2012 y Decreto
              1377 de 2013. Para conocer más sobre los derechos que les asisten a los titulares de los Datos Personales, las formas
              para ejercerlos y las finalidades y tipos de tratamiento a los que los Datos Personales serán sometidos, lo invitamos
              a consultar la Política de Tratamiento de la Información de la Compañía, disponible en:
            </Text>
            <Text style={styles.footerLinks}>
              Cali: https://www.ciudadlimpiacali.com.co/wp-content/uploads/documentos/politica_tratamiento_cali.pdf, Neiva:
              https://www.ciudadlimpianeiva.com.co/comunicados/PoliticasTratamientoDeLaInformacion.pdf, Huila:
              https://www.ciudadlimpiadelhuila.com.co/comunicados/PoliticasTratamientoDeLainformacion.pdf, Bogotá:
              https://www.ciudadlimpia.com.co/PoliticasTratamientodelainformacion.pdf
            </Text>
            <Text style={styles.footerCode}>BFCM06 - 01/2026 Ed. 01</Text>
          </View>

        </View>
      </Page>

      {/* ===== PÁGINA 2: CNP ===== */}
      {data?.cnpImage && (
        <Page size="LETTER" style={[styles.stampPage, { padding: 30, paddingTop: 25, justifyContent: 'center', alignItems: 'center' }]}>
          {/* Header de la página */}
          <View style={{ width: '100%', marginBottom: 20 }} fixed>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Image src={logoLimpia} style={{ width: 80, height: 28, objectFit: 'contain' }} />
              <Text style={{ fontSize: 7, color: '#6c757d' }}>
                Guía: {data?.guia} | {formatFecha(data?.fecha_informe)} {formatHora(data?.hora_visita)}
              </Text>
            </View>
            <Text style={{ fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#dc3545', textTransform: 'uppercase' }}>
              CNP
            </Text>
            <View style={{ borderBottomWidth: 1.5, borderBottomColor: '#dc3545', marginTop: 4 }} />
          </View>

          {/* CNP imagen - página completa */}
          <View style={{ width: '100%', height: 'auto', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Image
              src={resolveImgUrl(data.cnpImage)}
              style={{
                width: '90%',
                height: '100%',
                maxHeight: 600,
                objectFit: 'contain',
                borderWidth: 1,
                borderColor: '#dee2e6',
                borderRadius: 4,
                backgroundColor: '#f8f9fa'
              }}
            />
          </View>
        </Page>
      )}

      {/* ===== PÁGINA 3: EVIDENCIAS FOTOGRÁFICAS ===== */}
      {fotosImages.length > 0 && (
        <Page size="LETTER" style={[styles.stampPage, { padding: 30, paddingTop: 25, justifyContent: 'flex-start' }]} wrap>
          {/* Header de la página */}
          <View style={{ width: '100%', marginBottom: 12 }} fixed>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Image src={logoLimpia} style={{ width: 80, height: 28, objectFit: 'contain' }} />
              <Text style={{ fontSize: 7, color: '#6c757d' }}>
                Guía: {data?.guia} | {formatFecha(data?.fecha_informe)} {formatHora(data?.hora_visita)}
              </Text>
            </View>
            <Text style={{ fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#dc3545', textTransform: 'uppercase' }}>
              Evidencias Fotográficas del Caso
            </Text>
            <View style={{ borderBottomWidth: 1.5, borderBottomColor: '#dc3545', marginTop: 4 }} />
          </View>

          {/* Grid de fotos - 2 columnas */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
            {fotosImages.map((fotoImg, index) => (
              <View key={`foto-${index}`} style={{ width: '48%', marginBottom: 18, alignItems: 'center' }} wrap={false}>
                <Image
                  src={resolveImgUrl(fotoImg)}
                  style={{
                    width: '100%',
                    height: 240,
                    objectFit: 'contain',
                    borderWidth: 1,
                    borderColor: '#dee2e6',
                    borderRadius: 4,
                    backgroundColor: '#f8f9fa'
                  }}
                />
                <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#495057', marginTop: 4 }}>
                  Foto {index + 1} de {fotosImages.length}
                </Text>
              </View>
            ))}
          </View>
        </Page>
      )}
    </Document>
  );
};

export default TiqueteCorrespondenciaPDF;
